import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import * as Api from "../lib/api";
import { api as axiosApi } from "../lib/api";
import { CreditCard, Check, Loader2, ExternalLink, Beaker } from "lucide-react";
import { toast } from "sonner";

export default function BillingPage() {
  const { team, user, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [pollMsg, setPollMsg] = useState("");
  const [info, setInfo] = useState({ sandbox: false, price_per_seat_usd: 50 });

  useEffect(() => {
    axiosApi.get("/billing/info").then(r => setInfo(r.data)).catch(() => {});
  }, []);

  // Poll for payment status if returning from Stripe
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setPollMsg("Verifying payment…");
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const s = await Api.checkoutStatus(sid);
        if (s.payment_status === "paid") {
          setPollMsg("Payment confirmed");
          toast.success("Subscription active");
          await refresh();
          setTimeout(() => nav("/"), 800);
          return;
        }
        if (s.status === "expired") { setPollMsg("Session expired."); return; }
        if (attempts < 8) setTimeout(poll, 2000);
        else setPollMsg("Still pending — refresh in a moment.");
      } catch (e) {
        console.error(e);
        if (attempts < 5) setTimeout(poll, 2000);
        else setPollMsg("Error verifying payment.");
      }
    };
    poll();
  }, [params, nav, refresh]);

  async function startCheckout() {
    setBusy(true);
    try {
      const { url } = await Api.createCheckout();
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  }

  async function openPortal() {
    setBusy(true);
    try {
      const { url } = await Api.customerPortal();
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open portal");
      setBusy(false);
    }
  }

  const status = team?.stripe_status || "incomplete";
  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen bg-[#0A0A0A] bg-grid-dark">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="inline-block bg-[#F26522] text-black px-3 py-1 font-mono text-[11px] font-black tracking-[0.2em] uppercase mb-3">Billing</div>
          <h1 className="font-heading text-5xl sm:text-6xl font-black uppercase tracking-tight text-white leading-[0.95]">Subscription</h1>
          {info.sandbox && (
            <div className="bg-black border-2 border-[#F26522] p-3 mt-4 flex items-start gap-3" data-testid="sandbox-banner">
              <Beaker className="w-4 h-4 text-[#F26522] shrink-0 mt-0.5" />
              <div className="text-xs text-white/80">
                <strong className="text-[#F26522] uppercase tracking-wider font-bold">Sandbox Mode</strong> — billing is simulated for development. No card required, no real charges. Activate the sub to unlock the calculator end-to-end.
              </div>
            </div>
          )}
        </div>

        {pollMsg && (
          <div className="bg-[#F26522] text-black p-4 mb-6 font-bold uppercase tracking-wider text-sm flex items-center gap-2" data-testid="poll-msg">
            <Loader2 className="w-4 h-4 animate-spin" /> {pollMsg}
          </div>
        )}

        <div className="bg-[#111111] border-2 border-white/15 p-6 mb-6" data-testid="billing-status-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="font-mono text-[10px] tracking-[0.2em] text-[#F26522] uppercase">Team</div>
              <div className="font-heading text-2xl font-black uppercase text-white mt-1">{team?.name || "—"}</div>
            </div>
            <div className={`px-4 py-2 font-mono text-xs font-bold uppercase tracking-wider ${
              status === "active" ? "bg-[#10B981] text-black" :
              status === "past_due" ? "bg-[#F26522] text-black" :
              "bg-white/10 text-white"
            }`} data-testid="billing-status-badge">
              {status}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
            <Stat label="Per-seat price" value="$50 /mo" />
            <Stat label="Active seats" value={team?.seat_count ?? 0} />
            <Stat label="Today's charge" value={`$${((team?.seat_count || 0) * 50).toFixed(2)} /mo`} />
          </div>

          <div className="mt-6 text-sm text-white/70">
            <strong className="text-white">How seats work:</strong> Each technician you invite = 1 seat ($50/mo).
            {isAdmin && (
              <> You{user?.uses_calculator ? " (admin)" : ""} are{" "}
                <span className="text-[#F26522] font-bold">{user?.uses_calculator ? "INCLUDED" : "NOT INCLUDED"}</span> as a seat. Toggle this in <a href="/team" className="text-[#F26522] underline">Team</a>.</>
            )}
          </div>
        </div>

        {isAdmin && status !== "active" && (
          <button
            data-testid="btn-start-checkout"
            disabled={busy}
            onClick={startCheckout}
            className="w-full h-16 bg-[#F26522] text-black font-heading font-black tracking-wider uppercase text-base flex items-center justify-center gap-2 hover:bg-[#FF7A2A] transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
            Activate Subscription
          </button>
        )}

        {isAdmin && status === "active" && (
          <button
            data-testid="btn-customer-portal"
            disabled={busy}
            onClick={openPortal}
            className="w-full h-16 border-2 border-[#F26522] bg-transparent text-white font-heading font-black tracking-wider uppercase text-base flex items-center justify-center gap-2 hover:bg-[#F26522] hover:text-black transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ExternalLink className="w-5 h-5" />}
            Manage Card & Invoices
          </button>
        )}

        {!isAdmin && (
          <div className="bg-[#111111] border-2 border-white/15 p-5 text-white/70 text-sm">
            Only your team admin can manage billing. Ask them to activate the subscription if the calculator is locked.
          </div>
        )}

        {status === "active" && (
          <div className="bg-black border-l-4 border-[#10B981] p-4 mt-6 flex items-start gap-3" data-testid="billing-active-msg">
            <Check className="w-5 h-5 text-[#10B981] shrink-0 mt-0.5" />
            <div className="text-sm text-white">Your team's subscription is active. All techs can use the calculator.</div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-black border border-white/10 p-3">
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/50">{label}</div>
      <div className="font-mono text-xl font-medium text-white mt-1">{value}</div>
    </div>
  );
}
